import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Tippy from "@tippyjs/react";
import "tippy.js/dist/tippy.css";
import InfoPage from "../InfoPage";
import { CHECKOUT_TRIAL_TOOLTIP } from "../../utils/checkoutTrialTooltip";
import useTrustedCarouselProfiles from "../../hooks/useTrustedCarouselProfiles";
import IntroVideoPreview from "../../components/IntroVideoPreview";
import ExitIntentModal from "../../components/ExitIntentModal";
import './HostCheckout.sass';

const BasicPlanCard = ({ title, description }) => (
  <div className="plan-card" style={{ borderColor: '#0f084b', borderWidth: 2 }}>
    <div className="plan-title">{title}</div>
    <div className="plan-price">Free</div>
    <ul className="plan-features">
      {(description || []).map((line, idx) => (
        <li key={idx}>
          <span style={{ color: '#0f084b' }}>•</span>
          <span>{line}</span>
        </li>
      ))}
    </ul>
    <div className="plan-footer-container">
      <div className="plan-footer">No credit card required. Upgrade anytime.</div>
      <button type="button" className="plan-button" style={{ background: '#0f084b' }} onClick={() => {
        try {
          const params = new URLSearchParams(window.location.search);
          let email = params.get('email');
          if (!email) {
            try { email = localStorage.getItem('postaway_email') || ''; } catch (_) {}
          }
          if (!email) { alert('Missing email. Please go back to onboarding.'); return; }
          
          fetch('/api/membership/set-basic', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ email }),
          }).then(res => res.json()).then(data => {
            if (data.ok) {
              window.location.assign('/host-dashboard');
            } else {
              alert('Something went wrong. Please try again.');
            }
          }).catch(() => {
            alert('Something went wrong. Please try again.');
          });
        } catch (e) {
          alert('Something went wrong. Please try again.');
        }
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = '#1a1160'; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = '#0f084b'; }}
      >Get Started Free</button>
    </div>
  </div>
);

const PlanCard = ({ title, price, badge, description, planId }) => (
  <div className="plan-card">
    {badge && (
      <div className="plan-badge">{badge}</div>
    )}
    <div className="plan-title">{title}</div>
    <div className="plan-price">{price}</div>
    <ul className="plan-features">
      {(description || []).map((line, idx) => (
        <li key={idx}>
          <span style={{ color: '#0f084b' }}>•</span>
          <span>{line}</span>
        </li>
      ))}
    </ul>
    <div className="plan-footer-container">
      <div className="plan-footer">Plus processing fees.</div>
      <Tippy content={CHECKOUT_TRIAL_TOOLTIP} placement="top" maxWidth={380}>
        <div style={{ width: '100%' }}>
          <button type="button" className="plan-button" onClick={async () => {
            try {
              const params = new URLSearchParams(window.location.search);
              let email = params.get('email');
              if (!email) {
                try { email = localStorage.getItem('postaway_email') || ''; } catch (_) {}
              }
              if (!email) { alert('Missing email. Please go back to onboarding.'); return; }
              // Collect Rewardful referral id from cookie or JS API
              const rwRef = (window.Rewardful && window.Rewardful.referral) ||
                (document.cookie.match(/(?:^|;)\s*rw_ref=([^;]+)/)?.[1] ?? null);

              const res = await fetch('/api/stripe/checkout', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email, planId, rwRef }) });
              const data = await res.json();
              if (!res.ok || !data.ok) throw new Error('stripe_checkout_error');
              window.location.assign(data.url);
            } catch (e) {
              alert('Unable to start checkout. Please try again.');
            }
          }}>Continue</button>
        </div>
      </Tippy>
    </div>
  </div>
);

const HostCheckout = () => {
  const navigate = useNavigate();
  const [authorized, setAuthorized] = useState(false);
  const trustedProfiles = useTrustedCarouselProfiles();
  const profileCount = Math.max(trustedProfiles.length, 1);
  const checkoutDesktopDurationSeconds = Math.min(520, Math.max(52, Math.round(profileCount * 3.8)));
  const checkoutMobileDurationSeconds = Math.min(620, Math.max(68, Math.round(profileCount * 4.6)));
  const checkoutCarouselTimingVars = {
    "--checkout-carousel-duration-desktop": `${checkoutDesktopDurationSeconds}s`,
    "--checkout-carousel-duration-mobile": `${checkoutMobileDurationSeconds}s`,
  };

  // Gatekeeping: Ensure user has email before showing checkout page
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    let email = params.get('email');
    if (!email) {
      try {
        email = localStorage.getItem('postaway_email') || '';
      } catch (_) {}
    }

    if (!email) {
      // No email - redirect to host login
      console.warn('HostCheckout page: No email found, redirecting to login');
      navigate('/host-login');
      return;
    }

    // Email exists - allow access
    setAuthorized(true);
  }, [navigate]);

  // Don't render until authorized
  if (!authorized) {
    return null;
  }

  const header = (
    <div className="pricing-header">
      <h3>HOSTS, HOTELS & TOURISM BOARDS</h3>
      <p>Choose the membership that fits your portfolio size.</p>
      <div className="trusted-carousel" aria-label="Trusted hosts and creators">
        <div className="trusted-carousel-title">Trusted by top travel creators, photographers and hosts in the hospitality industry.</div>
        <div className="trusted-carousel-viewport">
          <div className="trusted-carousel-track" style={checkoutCarouselTimingVars}>
            {[...trustedProfiles, ...trustedProfiles].map((profile, idx) => (
              <div className="trusted-carousel-item" key={`${profile.handle}-${idx}`}>
                {profile.introVideoUrl ? (
                  <IntroVideoPreview
                    className="trusted-carousel-avatar"
                    mode="card"
                    posterUrl={profile.introVideoPosterUrl || profile.image}
                    publicId={profile.introVideoPublicId}
                    fullUrl={profile.introVideoUrl}
                    fallbackImageUrl={profile.image}
                    alt={profile.handle}
                  />
                ) : (
                  <img
                    src={profile.image}
                    alt={profile.handle}
                    className="trusted-carousel-avatar"
                    loading="lazy"
                  />
                )}
                <span className="trusted-carousel-handle">{profile.handle}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );

  const below = (
    <div className="pricing-container host-checkout-pricing-container">
      <div className="plans-grid host-checkout-plans-grid">
        <BasicPlanCard title="Basic Membership (Limited Access)" description={[
          '1 free property listing',
          'No platform fee on bookings',
          'Listed on Postaway hosts page',
        ]} />
        <PlanCard title="Round Trip Ticket" price="$99 / year" planId="host_round_trip_yearly" description={[
          'For hosts with 0–3 properties and Tourism Boards',
          'Property promotion on social media',
          'Access to Collab Board and Travel Board',
          'Messaging with creators',
          'Reviews and priority host retreats',
          'No platform fee on bookings',
        ]} />
        <PlanCard title="Passport" price="$299 / year" planId="host_passport_yearly" description={[
          'For hosts with 4–10 properties',
          'Everything in Round Trip, plus expanded listings',
        ]} />
        <PlanCard title="Visa" price="$399 / year" planId="host_visa_yearly" description={[
          'For hosts with 10+ properties and hotels',
          'Everything in Passport, plus premium placement',
        ]} />
      </div>
      <div className="pricing-footer">
        Global Entry — Invitation Only for full access and benefits to Postaway.
      </div>

      {/* Social proof */}
      <div className="social-proof-grid">
        {[{
          img: '/chloe-creator.png',
          name: 'Chloe, Creator',
          quote: '"As a travel content creator, I\'ve worked with a lot of vacation rental hosts — and keeping track of pitches, approvals, comped stays, deliverables, and posting dates used to be a total headache. Using Postaway\'s "My Collab Tracker" feature made the entire collaboration process incredibly smooth."'
        }, {
          img: '/jordan.png',
          name: 'Jordan, Host',
          quote: '"Postaway made finding the right creator to collaborate with incredibly easy. Instead of spending hours searching and vetting influencers, I was matched with a quality content creator whose audience aligned perfectly with my property. The process was smooth, communication was clear, and we saw a noticeable boost in awareness - and even a few direct bookings - shortly after the collaboration."'
        }, {
          img: '/hayden.png',
          name: 'Hayden, Traveler',
          quote: '"I was overwhelmed planning my New Zealand trip, but Postaway made it effortless. I found the perfect itinerary tailored to exactly what I wanted — adventure, scenery, and unique stays all in one seamless plan. It saved me hours of research and made me even more excited for the trip."'
        }].map((t, i) => (
          <div key={i} className="social-proof-card">
            <img src={t.img} alt={t.name} />
            <div>
              <div className="social-proof-name">{t.name}</div>
              <div className="social-proof-quote">{t.quote}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      <ExitIntentModal />
      <InfoPage belowHeader={header} below={below} disableStamps tallOverride={false} customMinHeight={1040} />
    </>
  );
};

export default HostCheckout;


